import { Formik, Form } from "formik";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Modal } from "react-bootstrap"
import { useTranslation } from "react-i18next";
import TextBox from "../../../controls/form/TextBox";
import Forms from "../../../../services/forms";
import * as Yup from "yup";
import Dropdown from "../../../controls/form/Dropdown";
import DatePicker from "../../../controls/form/DatePicker";

const EditFormInstance = ({
    show,
    formInstance,
    formTemplates,
    dropdownUsers,
    dateFormat,
    onAdd,
    onClose
}) => {
    const { id: deploymentId } = useParams();
    const [translate] = useTranslation();
    const [showSaveButton, setShowSaveButton] = useState(false);

    const handleOnChange = useCallback(() => {
        setShowSaveButton(true);
    }, []);

    const updateFormInstance = useCallback(async ({ name, templateId, userId, dueDate, expiryDate }) => {
        let newId;
        if (!formInstance?._id) {
            const response = await Forms.addFormInstance({
                deploymentId,
                body: { name, templateId, userId, dueDate, expiryDate }
            });
            if (!response.error && response.data) {
                newId = response.data._id;
            }
        }
        else {
            await Forms.editFormInstance({
                deploymentId,
                formInstanceId: formInstance._id,
                body: { name, templateId, userId, dueDate, expiryDate }
            });
        }
        onAdd(newId);
    }, [deploymentId, formInstance, onAdd]);

    const renderModalHeading = useMemo(() => {
        return <label className="h5">{translate(!formInstance?._id ? 'OTM_ADD_FORM_INSTANCE' : 'OTM_MANAGE_FORM_INSTANCE')}</label>
    }, [formInstance, translate]);

    const templateNamesOptions = useMemo(() => {
        return Array.from(new Set(formTemplates.map(template => template.name))).map(name => ({ _id: name, name }));
    }, [formTemplates]);

    const renderForm = useMemo(() => {
        return <Formik initialValues={{
            name: formInstance?.name || "",
            templateName: formInstance?.templateId?._id ? formTemplates.find(template => template._id === formInstance?.templateId?._id).name : null,
            templateId: formInstance?.templateId?._id || null,
            userId: formInstance?.userId?._id || null,
            dueDate: formInstance?.dueDate ? new Date(formInstance?.dueDate) : null,
            expiryDate: formInstance?.expiryDate ? new Date(formInstance?.expiryDate) : null,
        }} onSubmit={updateFormInstance}
            validateOnMount
            validationSchema={Yup.object({
                name: Yup.string().required(translate("OTM_FORM_INSTANCE_NAME_REQUIRED")),
                templateName: Yup.string().nullable().required(translate("OTM_FORM_TEMPLATE_REQUIRED")),
                userId: Yup.string().nullable().required(translate("OTM_FORM_USER_REQUIRED")),
                dueDate: Yup.date().nullable().required(translate("OTM_FORM_DUE_DATE_REQUIRED"))
            })}
            enableReinitialize>
            {(props) => <Form onChange={handleOnChange} className="flex-grow-1 d-flex flex-column">
                <Modal.Header>
                    {renderModalHeading}
                </Modal.Header>

                <Modal.Body>
                    <div className="row mt-n3 align-items-start">
                        <div className='col-12 mt-3'>
                            <Dropdown
                                isRequired={true}
                                heading={translate("OTM_FORM_TEMPLATE")}
                                name="templateName"
                                valueKey="_id"
                                displayValueKey="version"
                                onSelect={(value) => {
                                    const filteredTemplates = formTemplates.filter(template => template.name === value)
                                    const activeVersion = filteredTemplates.find(template => template.isActive);

                                    const templateId = activeVersion || filteredTemplates.sort((a, b) => a.version - b.version).findLast(template => template.isPublished);
                                    props.setFieldValue("templateId", templateId._id);
                                    handleOnChange();
                                }}
                                options={templateNamesOptions}
                                grow={true}
                                readOnly={formInstance?._id} />
                        </div>
                        <div className='col-12 mt-3'>
                            <Dropdown
                                isRequired={true}
                                heading={translate("OTM_USER")}
                                onSelect={handleOnChange}
                                name="userId"
                                valueKey="_id"
                                displayValueKey="name"
                                options={dropdownUsers}
                                grow={true} />
                        </div>
                        <div className='col-12 mt-3'>
                            <TextBox isRequired={true} label={translate("OTM_FORM_INSTANCE_NAME")} type="text" name="name" />
                        </div>
                        <div className='col-12 col-lg-6 mt-3'>
                            <DatePicker
                                name={"dueDate"}
                                label={translate("OTM_DUE_DATE")}
                                showTimeSelect={false}
                                isRequired={true}
                                dateFormat={dateFormat}
                                onChange={(newDate) => {
                                    if (newDate) {
                                        props.setFieldValue("dueDate", new Date(newDate.setHours(23, 59, 59)))
                                    }
                                    handleOnChange();
                                }} />
                        </div>
                        <div className='col-12 col-lg-6 mt-3'>
                            <DatePicker
                                name={"expiryDate"}
                                label={translate("OTM_EXPIRY_DATE")}
                                showTimeSelect={false}
                                dateFormat={dateFormat}
                                onChange={(newDate) => {
                                    if (newDate) {
                                        props.setFieldValue("expiryDate", new Date(newDate.setHours(23, 59, 59)))
                                    }
                                    handleOnChange();
                                }} />
                        </div>
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    {showSaveButton && props.isValid && <Button size='sm' variant='primary' type='submit'>{translate('OTM_SAVE')} </Button>}
                    <Button size='sm' variant='secondary' onClick={onClose}>{translate('OTM_CANCEL')} </Button>
                </Modal.Footer>
            </Form>}
        </Formik>
    }, [onClose, formInstance, dateFormat, renderModalHeading, templateNamesOptions, formTemplates, dropdownUsers, updateFormInstance, translate, handleOnChange, showSaveButton]);

    return <Modal
        size={'lg'}
        show={show}
        backdrop={'static'}>
        {renderForm}
    </Modal>
}

export default EditFormInstance;